import { type AxiosResponse, isAxiosError } from 'axios'

import {
  type GkAirApiGetGkAirRecommendationsWebRequest,
  type GkAirProxyServiceWebResponse,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import { apmCaptureError } from '../../../apm'
import { type UdpTrackingMetadata } from '../../../udp-tracking'
import { recommendationResponseDataLayerPush } from '../../tracking/recommendationResponseTracking'

type GkAirRecommendationClient = {
  getGkAirRecommendationsWeb: (
    requestParams: GkAirApiGetGkAirRecommendationsWebRequest,
  ) => Promise<AxiosResponse<GkAirProxyServiceWebResponse>>
}

type ProdrecRecommendationClient = {
  getProdrecRecommendationsWeb: (
    requestParams: GkAirApiGetGkAirRecommendationsWebRequest,
  ) => Promise<AxiosResponse<GkAirProxyServiceWebResponse>>
}

const LATENCY_PRECISION_MULTIPLIER = 10

const calculateRecommendationRequestLatency = ({
  endTime,
  startTime,
}: {
  endTime: number
  startTime: number
}): number =>
  Math.round((endTime - startTime) * LATENCY_PRECISION_MULTIPLIER) /
  LATENCY_PRECISION_MULTIPLIER

type SafelyPushRecommendationResponseParams = Omit<
  Parameters<typeof recommendationResponseDataLayerPush>[0],
  'metadata'
> & {
  buildMetadata: () => UdpTrackingMetadata
}

const safelyPushRecommendationResponse = ({
  buildMetadata,
  ...params
}: SafelyPushRecommendationResponseParams): void => {
  try {
    recommendationResponseDataLayerPush({
      ...params,
      metadata: buildMetadata(),
    })
  } catch (error) {
    const errorInstance = Error.isError(error)
      ? error
      : new Error('Failed to track recommendation request response')

    apmCaptureError(errorInstance)
  }
}

export const recommendationFetchService = async ({
  buildMetadata,
  gkAirClient,
  isProdrecCall,
  prodrecClient,
  requestParams,
}: {
  buildMetadata: () => UdpTrackingMetadata
  gkAirClient: GkAirRecommendationClient
  isProdrecCall: boolean
  prodrecClient: ProdrecRecommendationClient
  requestParams: GkAirApiGetGkAirRecommendationsWebRequest
}): Promise<GkAirProxyServiceWebResponse> => {
  const requestId = `req_${crypto.randomUUID()}`
  const startTime = performance.now()
  const responsePromise = isProdrecCall
    ? prodrecClient.getProdrecRecommendationsWeb(requestParams)
    : gkAirClient.getGkAirRecommendationsWeb(requestParams)
  let response: AxiosResponse<GkAirProxyServiceWebResponse> | undefined
  let responseStatusCode: number | null = null

  try {
    response = await responsePromise

    return response.data
  } catch (error) {
    responseStatusCode = isAxiosError(error)
      ? (error.response?.status ?? null)
      : null

    throw error
  } finally {
    safelyPushRecommendationResponse({
      buildMetadata,
      contextProductId: requestParams.pid,
      latency: calculateRecommendationRequestLatency({
        endTime: performance.now(),
        startTime,
      }),
      requestId,
      response: response?.data,
      responseStatusCode: response?.status ?? responseStatusCode,
      serviceId: requestParams.serviceId,
    })
  }
}
